import { generateId as defaultGenerateId, readStorage, writeStorage } from "./environment";
import type { EndedSession } from "./types";

const SESSION_KEY = "business-analytics.session";

interface SessionState {
	id: string;
	startedAt: string;
	lastActivityAt: number;
}

export interface SessionStamp {
	sessionId: string;
	startedAt: string;
	/** True only on the call that minted this session, i.e. when to register it upstream. */
	isNew: boolean;
	/**
	 * The session this call replaced, when one timed out. Its end is the last activity we
	 * recorded, not now — the gap between the two is precisely the inactivity that ended it.
	 */
	endedSession?: EndedSession;
}

export interface SessionManagerOptions {
	timeoutMs: number;
	/** Injectable so the boundary logic can be exercised without clocks or storage. */
	now?: () => number;
	generateId?: () => string;
	read?: () => string | null;
	write?: (value: string) => void;
}

/**
 * Per-tab sessions. State lives in sessionStorage, which gives the intended semantics for
 * free: independent sessions per tab, surviving reload, gone on a real tab close.
 *
 * Session boundaries are decided lazily, on the next stamp() — never eagerly on a timer or
 * a visibility event — so a quick tab switch never costs a session.
 */
export class SessionManager {
	private readonly timeoutMs: number;
	private readonly now: () => number;
	private readonly generateId: () => string;
	private readonly read: () => string | null;
	private readonly write: (value: string) => void;
	private state: SessionState | null = null;

	constructor(options: SessionManagerOptions) {
		this.timeoutMs = options.timeoutMs;
		this.now = options.now ?? (() => Date.now());
		this.generateId = options.generateId ?? defaultGenerateId;
		this.read = options.read ?? (() => readStorage("session", SESSION_KEY));
		this.write = options.write ?? (value => writeStorage("session", SESSION_KEY, value));
	}

	/**
	 * The current session id, minting a new one first if the inactivity window has elapsed.
	 * Records activity on every call, so an idle-but-open tab times out just like a hidden one.
	 */
	stamp(): SessionStamp {
		const now = this.now();
		// Storage is the source of truth, re-read every time: another page in this tab may have
		// written it, and a cached copy would quietly diverge. The in-memory state is only a
		// fallback for when storage is unavailable, where it is all we have.
		const current = this.restore() ?? this.state;

		if (!current || now - current.lastActivityAt > this.timeoutMs) {
			const replaced: EndedSession | undefined = current
				? { sessionId: current.id, endedAt: new Date(current.lastActivityAt).toISOString() }
				: undefined;

			return this.persist({ id: this.generateId(), startedAt: new Date(now).toISOString(), lastActivityAt: now }, true, replaced);
		}

		return this.persist({ ...current, lastActivityAt: now }, false);
	}

	private persist(state: SessionState, isNew: boolean, endedSession?: EndedSession): SessionStamp {
		this.state = state;
		this.write(JSON.stringify(state));
		return { sessionId: state.id, startedAt: state.startedAt, isNew, endedSession };
	}

	/** Anything unreadable or malformed is treated as "no session", which mints a fresh one. */
	private restore(): SessionState | null {
		const raw = this.read();
		if (!raw) return null;

		try {
			const parsed = JSON.parse(raw) as Partial<SessionState>;
			if (typeof parsed.id !== "string" || typeof parsed.startedAt !== "string" || typeof parsed.lastActivityAt !== "number") {
				return null;
			}
			return { id: parsed.id, startedAt: parsed.startedAt, lastActivityAt: parsed.lastActivityAt };
		} catch {
			return null;
		}
	}
}
